import React from "react";
import siteConfig from "../config/siteConfig";
import "../css/delivery.scss";
import "../css/returns.scss";
import PolicySidebar from "./PolicySidebar";

function Returns() {
  return (
    <main className="policy-page policy-page--returns">
      <header className="policy-hero">
        <div>
          <p className="policy-eyebrow">Customer care · {siteConfig.brandName}</p>
          <h1>Returns &amp; refunds</h1>
        </div>
        <div className="policy-hero__intro">
          <p>
            Guidance for changing your mind, reporting damage and understanding
            how an approved refund is handled.
          </p>
          <span>Last updated 17 October 2026</span>
        </div>
      </header>

      <div className="policy-shell">
        <PolicySidebar
          activePath="/returns"
          tableOfContents={[
            { id: "returns-window", label: "Return window" },
            { id: "returns-start", label: "Start a return" },
            { id: "returns-condition", label: "Item condition" },
            { id: "returns-refunds", label: "Refunds" },
            { id: "returns-damage", label: "Damage & exceptions" },
          ]}
        />

        <article className="policy-content">
          <div className="policy-demo-note" role="note">
            <strong>Contact us before sending a return</strong>
            <p>
              Email {siteConfig.contact.email} with the order reference and item before
              posting anything. We will confirm eligibility and the correct return
              instructions. Mandatory consumer rights remain unaffected.
            </p>
          </div>

          <section id="returns-window">
            <p className="policy-section-number">01</p>
            <h2>A 30-day return window</h2>
            <p>
              A change-of-mind return request may be made within 30 calendar days of
              confirmed delivery. Items should remain unused, complete and in their
              original saleable condition. This does not limit any mandatory consumer
              rights that may apply in a buyer’s location.
            </p>
            <div className="policy-pullquote">
              <strong>30 days</strong>
              <span>From confirmed delivery to the return request.</span>
            </div>
          </section>

          <section id="returns-start">
            <p className="policy-section-number">02</p>
            <h2>How to begin a return</h2>
            <ol>
              <li>
                <strong>Contact customer care.</strong> Include the order reference and
                the item you wish to return.
              </li>
              <li>
                <strong>Wait for instructions.</strong> We will confirm
                eligibility and provide the correct return address or label.
              </li>
              <li>
                <strong>Pack it carefully.</strong> Reuse the original protective
                packaging and retain proof of postage until the return is resolved.
              </li>
            </ol>
            <p>
              Parcels sent without confirmation may be delayed or sent to the wrong
              facility. Email <a href={`mailto:${siteConfig.contact.email}`}>{siteConfig.contact.email}</a>
              {" "}to make the request.
            </p>
          </section>

          <section id="returns-condition">
            <p className="policy-section-number">03</p>
            <h2>Condition &amp; careful packaging</h2>
            <div className="policy-cards">
              <div className="policy-card">
                <span>Returnable condition</span>
                <strong>Unused and complete</strong>
                <p>Include original components, inserts and protective packaging.</p>
              </div>
              <div className="policy-card">
                <span>Handle with care</span>
                <strong>Protect every surface</strong>
                <p>Fragile pieces should not move inside the outer shipping box.</p>
              </div>
            </div>
            <p>
              We may reduce a refund when handling goes beyond what
              is reasonably necessary to inspect an item, where permitted by law.
            </p>
          </section>

          <section id="returns-refunds">
            <p className="policy-section-number">04</p>
            <h2>Inspection &amp; refunds</h2>
            <p>
              After arrival, the return is inspected and its outcome
              confirmed by email. Approved refunds are sent to the original
              payment method. Banks and payment providers can require an additional
              5–10 business days to display funds after a refund is initiated.
            </p>
            <p>
              Original expedited delivery charges and return postage are
              generally not refundable unless required by law or the item was faulty.
            </p>
          </section>

          <section id="returns-damage">
            <p className="policy-section-number">05</p>
            <h2>Damage, faults &amp; exceptions</h2>
            <p>
              For damage or an incorrect item, keep all packaging and send clear images
              promptly. Handmade variation, natural texture and minor colour differences
              between a screen and a physical object are not automatically defects.
            </p>
            <p>The following categories may be excluded from change-of-mind returns:</p>
            <ul>
              <li>Personalised, made-to-order or altered objects.</li>
              <li>Gift cards and clearly marked final-sale items, where lawful.</li>
              <li>Used goods or incomplete sets that cannot be resold safely.</li>
            </ul>
            <div className="policy-contact-box">
              <div>
                <span>Questions about an object?</span>
                <strong>{siteConfig.contact.email}</strong>
              </div>
              <a href={`mailto:${siteConfig.contact.email}`}>Contact customer care</a>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
}

export default Returns;
